import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Building2, Tag, Phone, Mail } from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PortfolioDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setItem(null);
    api
      .get(`/portfolios/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (!item) return;
    api
      .get("/portfolios", { params: { limit: 50 } })
      .then((r) => {
        const list = asArray(r.data);
        const same = list
          .filter((p) => p.slug !== item.slug && p.category === item.category)
          .slice(0, 3);
        const fill = list
          .filter((p) => p.slug !== item.slug && !same.includes(p))
          .slice(0, 3 - same.length);
        setRelated([...same, ...fill].slice(0, 3));
      })
      .catch(() => setRelated([]));
  }, [item]);

  useSeo({
    title: item?.title,
    description: item?.summary,
    image: item?.image_url,
    path: `/portfolio/${slug}`,
    jsonLd: item && {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: item.title,
      description: item.summary || item.description,
      image: item.image_url,
      dateCreated: String(item.year),
      creator: { "@type": "Organization", name: "CV. Komunika Teknik" },
      about: item.category,
      keywords: (item.tags || []).join(", "),
    },
  });

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <div className="flex-1 grid place-items-center kt-section" data-testid="portfolio-not-found">
          <div className="text-center">
            <div className="kt-eyebrow">404</div>
            <h1 className="font-display text-3xl font-bold mt-4">
              Portfolio tidak ditemukan
            </h1>
            <button
              onClick={() => navigate("/portfolio")}
              className="kt-btn-primary mt-6"
            >
              <ArrowLeft size={16} /> Kembali ke Portfolio
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <div className="flex-1 grid place-items-center text-[#52525B] font-mono-kt text-sm">
          Memuat…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="portfolio-detail-page">
      <Header />

      {/* HERO */}
      <section className="bg-[#0F172A] text-white pt-24 pb-16 relative overflow-hidden">
        <div className="kt-container relative">
          <Link
            to="/portfolio"
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-2 mb-8"
            data-testid="portfolio-detail-back-link"
          >
            <ArrowLeft size={14} /> Semua Portfolio
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#FCA5A5]">
                <span>{item.category}</span>
                <span className="w-1 h-1 bg-[#DC2626] rounded-full" />
                <span className="font-mono-kt text-zinc-400">{item.year}</span>
              </div>
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05]">
                {item.title}
              </h1>
              {item.client && (
                <div className="mt-5 inline-flex items-center gap-2 text-zinc-300">
                  <Building2 size={16} className="text-[#DC2626]" />
                  <span className="text-sm">Klien: <strong className="text-white">{item.client}</strong></span>
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-px bg-white/10">
                <div className="bg-[#0F172A] p-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Tahun</div>
                  <div className="kt-stat text-2xl text-white mt-2 flex items-center gap-2">
                    <Calendar size={18} className="text-[#DC2626]" /> {item.year}
                  </div>
                </div>
                <div className="bg-[#0F172A] p-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Kategori</div>
                  <div className="text-sm font-semibold mt-2 leading-tight">
                    {item.category}
                  </div>
                </div>
                {item.tags?.length > 0 && (
                  <div className="col-span-2 bg-[#0F172A] p-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 flex items-center gap-2">
                      <Tag size={12} /> Tags
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-1 bg-white/10 text-zinc-200"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COVER IMAGE */}
      {item.image_url && (
        <section className="bg-white">
          <div className="kt-container -mt-10 relative">
            <div className="aspect-[16/9] overflow-hidden border border-[#E4E4E7] bg-[#0F172A]">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* BODY */}
      <section className="py-16 md:py-24 bg-white">
        <div className="kt-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {item.summary && (
              <p className="font-display text-xl md:text-2xl text-[#18181B] leading-snug font-medium">
                {item.summary}
              </p>
            )}
            <div className="kt-divider-red mt-6" />

            <div className="mt-8">
              <div className="kt-eyebrow">Ringkasan Proyek</div>
              <p className="mt-4 text-[#18181B] text-base md:text-lg leading-relaxed whitespace-pre-wrap">
                {item.description}
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { label: "Klien", value: item.client || "—" },
                { label: "Kategori", value: item.category },
                { label: "Tahun", value: item.year },
              ].map((d) => (
                <div key={d.label} className="border-l-2 border-[#DC2626] pl-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                    {d.label}
                  </div>
                  <div className="font-semibold mt-2">{d.value}</div>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="kt-card p-7 bg-[#FAFAFA]">
              <div className="kt-eyebrow">Punya Proyek Serupa?</div>
              <h3 className="font-display text-xl font-bold mt-3 leading-tight">
                Diskusikan dengan tim kami
              </h3>
              <p className="mt-3 text-sm text-[#52525B] leading-relaxed">
                Kami dapat mereplikasi atau menyesuaikan pendekatan dari proyek
                ini untuk kebutuhan organisasi Anda.
              </p>
              <Link
                to="/kontak"
                className="kt-btn-primary mt-6 w-full justify-center"
                data-testid="portfolio-detail-cta-button"
              >
                Hubungi Kami <ArrowRight size={16} />
              </Link>

              <div className="mt-6 pt-6 border-t border-[#E4E4E7] space-y-3 text-sm">
                <a
                  href="mailto:info@komunikateknik.online"
                  className="flex items-center gap-3 text-[#18181B] hover:text-[#DC2626]"
                >
                  <Mail size={16} className="text-[#DC2626]" />
                  info@komunikateknik.online
                </a>
                <a
                  href="tel:+6285246286550"
                  className="flex items-center gap-3 text-[#18181B] hover:text-[#DC2626]"
                >
                  <Phone size={16} className="text-[#DC2626]" />
                  +62 852-4628-6550
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="kt-section bg-[#FAFAFA] border-t border-[#E4E4E7]">
          <div className="kt-container">
            <div className="kt-eyebrow">Proyek Terkait</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mt-3">
              Karya lain yang mungkin relevan
            </h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/portfolio/${p.slug}`}
                  className="kt-card group overflow-hidden"
                  data-testid={`related-portfolio-${p.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#52525B]">
                      <span>{p.category}</span>
                      <span className="w-1 h-1 bg-[#DC2626] rounded-full" />
                      <span>{p.year}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg mt-3 leading-snug">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default PortfolioDetail;
